import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as songsApi from "../../api/songsApi";
import { failureMessage, isNotFound } from "../../api/client";
import { showNotice } from "../../actions/noticeActions";

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};

/** Issue reports sent from the song pages, for the host to review and resolve. */
const IssuesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [issues, setIssues] = useState(null); // null while loading
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0); // bumped by "Try again"

  useEffect(() => {
    let cancelled = false;
    songsApi.getIssues().then(
      (open) => {
        if (cancelled) return;
        setIssues(open);
        setFailed(false);
      },
      (error) => {
        if (cancelled) return;
        console.error("Could not load the issue reports", error);
        setIssues([]);
        setFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const resolve = async (issue) => {
    try {
      await songsApi.resolveIssue(issue.Id);
      setIssues((current) => current.filter((i) => i.Id !== issue.Id));
    } catch (error) {
      if (isNotFound(error)) {
        // resolved elsewhere meanwhile, or no longer in this host's scope: it is gone either way
        setIssues((current) => current.filter((i) => i.Id !== issue.Id));
        dispatch(showNotice("That report had already been dealt with.", "info"));
        return;
      }
      console.error("Could not resolve the issue report", error);
      dispatch(showNotice(failureMessage(error, "Couldn't resolve the report. Check your connection and try again.")));
    }
  };

  let body;
  if (issues === null) body = <div className="loading">Loading reports…</div>;
  else if (failed)
    body = (
      <div className="empty">
        Couldn’t load the reports.{" "}
        <button type="button" className="link-btn" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </button>
      </div>
    );
  else if (issues.length === 0) body = <div className="empty">No open reports. Problems reported from a song’s page show up here.</div>;
  else
    body = (
      <ol className="stack stack-tight">
        {issues.map((issue) => (
          <li key={issue.Id} className="issue">
            {issue.SongId ? (
              <button type="button" className="issue-title" style={{ textAlign: "left" }} onClick={() => navigate(`/songs/${issue.SongId}`)}>
                {issue.SongTitle}
              </button>
            ) : (
              <span className="issue-title">
                {issue.SongTitle} <span className="small muted">(song deleted)</span>
              </span>
            )}
            {issue.IssueTypes && <div className="issue-types">{issue.IssueTypes}</div>}
            {issue.Description && <p className="issue-desc">{issue.Description}</p>}
            <div className="issue-foot">
              <span className="issue-date">{formatDate(issue.Reported)}</span>
              <button type="button" className="btn btn-xs btn-outline" onClick={() => resolve(issue)}>
                Resolved
              </button>
            </div>
          </li>
        ))}
      </ol>
    );

  return (
    <div className="page" data-screen-label="Issues">
      <button type="button" className="back-link" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <div className="page-head">
        <h2 className="page-title">Reported issues</h2>
      </div>
      {body}
    </div>
  );
};

export default IssuesPage;
